import type { DocumentView } from '~/types/tShared';
import { DocumentPatchInput } from '@tempus/trials-types';
import { AxiosResponse, isAxiosError } from 'axios';

import { parseUprDownloadJsonResponse, UprDocumentParseError } from '~/utils/uprDocumentPdf';
import { DocumentFileVersion, DocumentWithoutExtras } from '~/store/documentEdit/types';

import axios from '../axios';
import { TRIALS_API_ROOT, TRIALS_NEST_API_ROOT, UPLOAD_TIMEOUT, V1_DOCUMENTS } from '../constants';
import { DocumentVersionReviewData } from '../types/portal';
import { UprDocumentDownloadError, UprDownloadResult } from '../types/uprDocuments';

export const documentsApi = {
  getTrialDocuments: async (
    trialId: string,
    params?: Record<string, unknown>,
  ): Promise<AxiosResponse<DocumentView[]>> => {
    return axios.get(`${V1_DOCUMENTS}/trial/${trialId}`, { params });
  },
  getTempDocVersionId: async (versionId: string): Promise<{ id: string }> => {
    return axios.get(`${V1_DOCUMENTS}/${versionId}/temporary`).then(({ data }) => data);
  },
  generateTempDocDownloadUrl: (tempVersionId: string, fileName: string): string => {
    return `${V1_DOCUMENTS}/temporary/${tempVersionId}/${fileName}`;
  },
  getSite: async (siteId: string): Promise<AxiosResponse<DocumentView[]>> => {
    return axios.get(`${V1_DOCUMENTS}/site/${siteId}`);
  },
  latestApplicableAtByTrial: async (): Promise<Record<string, string>> => {
    return axios
      .get(`${V1_DOCUMENTS}/trial/latest-applicable-at`)
      .then(({ data }) => data)
      .catch(() => ({}));
  },
  create: async (body: {
    data: string;
    document:
      | string
      | (Omit<DocumentWithoutExtras, 'id' | 'versions' | 'tags'> & {
          tags: Array<string[]>;
        });
    version: Omit<DocumentFileVersion, 'id' | 'documentId'>;
  }): Promise<AxiosResponse<DocumentView>> => {
    return axios.post(`${V1_DOCUMENTS}/`, body, { timeout: UPLOAD_TIMEOUT });
  },
  deleteVersion: async (documentVersionId: string): Promise<boolean> => {
    return axios
      .delete(`${V1_DOCUMENTS}/${documentVersionId}`)
      .then(() => true)
      .catch(() => false);
  },
  patchVersion: async (documentVersionId: string, updates: DocumentPatchInput): Promise<DocumentView | false> => {
    return axios
      .patch(`${V1_DOCUMENTS}/${documentVersionId}`, updates)
      .then(({ data }) => data)
      .catch(() => false);
  },
  getVersionReview: async (documentVersionId: string): Promise<DocumentVersionReviewData | false> => {
    return axios
      .get(`${V1_DOCUMENTS}/${documentVersionId}/review`)
      .then(({ data }) => data)
      .catch(() => false);
  },
  getTags: async (): Promise<string[]> => {
    return axios
      .get(`${TRIALS_API_ROOT}/v1/documentTags`)
      .then(({ data }) => data)
      .catch(() => []);
  },
  getValuesForTag: async (key: string): Promise<string[]> => {
    return axios
      .get(`${TRIALS_API_ROOT}/v1/documentTags/${key}`)
      .then(({ data }) => data)
      .catch(() => []);
  },
  /** Nest: `GET …/documents/upr-download/document/:documentId/institution/:institutionId` */
  getUprDownload: async (institutionId: string, documentId: string): Promise<UprDownloadResult> => {
    const url = `${TRIALS_NEST_API_ROOT}/documents/upr-download/document/${encodeURIComponent(
      documentId,
    )}/institution/${encodeURIComponent(institutionId)}`;
    try {
      const { data } = await axios.get<unknown>(url, { responseType: 'json' });
      return parseUprDownloadJsonResponse(data);
    } catch (e) {
      if (e instanceof UprDocumentParseError) {
        throw e;
      }
      if (isAxiosError(e)) {
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          throw new UprDocumentDownloadError(status, 'Not authorized to view this document.');
        }
        if (status === 404) {
          throw new UprDocumentDownloadError(404, 'Document not found.');
        }
      }
      throw e;
    }
  },
};
